import React from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@/context/ThemeContext';
import { JourneyProvider } from '@/context/JourneyContext';
import { CompareProvider } from '@/context/CompareContext';
import { AuthProvider } from '@/context/AuthContext';
import BottomNav from '@/components/layout/BottomNav';
import Home from '@/pages/Home';
import Journey from '@/pages/Journey';
import JourneyIntro from '@/pages/JourneyIntro';
import JourneyFlow from '@/pages/JourneyFlow';
import DecisionQuiz from '@/pages/DecisionQuiz';
import Learn from '@/pages/Learn';
import LearnLesson from '@/pages/LearnLesson';
import PublicTools from '@/pages/PublicTools';
import CompareCars from '@/pages/CompareCars';
import CompareResults from '@/pages/CompareResults';
import OTDQuoteRequest from '@/pages/OTDQuoteRequest';
import OTDEmailGenerator from '@/pages/OTDEmailGenerator';
import OTDQuoteEmailer from '@/pages/OTDQuoteEmailer';
import OTDEmailerPage from '@/pages/OTDEmailerPage';
import TradeInBreakEven from '@/pages/TradeInBreakEven';
import BudgetTool from '@/pages/BudgetTool';
import CommunityBenchmarks from '@/pages/CommunityBenchmarks';
import Settings from '@/pages/Settings';
import ScanIntro from '@/pages/ScanIntro';
import OfferSiteWebview from '@/pages/OfferSiteWebview';
import Account from '@/pages/Account';
import Paywall from '@/pages/Paywall';
import '@/App.css';

// Routes that should NOT show the bottom navigation
const STANDALONE_ROUTES = ['/public-tools', '/tools'];

// Wrapper component to conditionally render BottomNav
const AppLayout = () => {
  const location = useLocation();
  const isStandalone = STANDALONE_ROUTES.some(route => location.pathname.startsWith(route));

  return (
    <div className="App min-h-screen bg-white dark:bg-gray-950 transition-colors">
      <Routes>
        {/* Default route - redirect to public-tools */}
        <Route path="/" element={<Navigate to="/public-tools" replace />} />
        
        {/* Redirect non-tools routes to public-tools */}
        <Route path="/account" element={<Navigate to="/public-tools" replace />} />
        <Route path="/paywall" element={<Navigate to="/public-tools" replace />} />
        <Route path="/journey-intro" element={<Navigate to="/public-tools" replace />} />
        <Route path="/journey" element={<Navigate to="/public-tools" replace />} />
        <Route path="/journey/*" element={<Navigate to="/public-tools" replace />} />
        <Route path="/quiz" element={<Navigate to="/public-tools" replace />} />
        <Route path="/learn" element={<Navigate to="/public-tools" replace />} />
        <Route path="/learn/*" element={<Navigate to="/public-tools" replace />} />
        <Route path="/settings" element={<Navigate to="/public-tools" replace />} />
        <Route path="/scan" element={<Navigate to="/public-tools" replace />} />
        <Route path="/vinny" element={<Navigate to="/public-tools" replace />} />
        <Route path="/vinny/*" element={<Navigate to="/public-tools" replace />} />
        
        {/* Redirect old /tools route to /public-tools */}
        <Route path="/tools" element={<Navigate to="/public-tools" replace />} />
        
        {/* Tools sub-pages - keep these accessible */}
        <Route path="/tools/compare-cars" element={<CompareCars />} />
        <Route path="/tools/compare-results" element={<CompareResults />} />
        <Route path="/tools/otd-quote-request" element={<OTDQuoteRequest />} />
        <Route path="/tools/otd-email-generate" element={<OTDEmailGenerator />} />
        <Route path="/tools/otd-quote-emailer" element={<OTDQuoteEmailer />} />
        <Route path="/tools/trade-break-even" element={<TradeInBreakEven />} />
        <Route path="/tools/budget" element={<BudgetTool />} />
        <Route path="/community-benchmarks" element={<CommunityBenchmarks />} />
        <Route path="/offer-site" element={<OfferSiteWebview />} />
        
        {/* Standalone Public Tools page - main landing */}
        <Route path="/public-tools" element={<PublicTools />} />
        <Route path="/public-tools/otd-emailer" element={<OTDEmailerPage />} />
        
        {/* Placeholder routes */}
        <Route path="/privacy" element={
          <div className="min-h-screen bg-gray-50 dark:bg-gray-950 p-8 pb-24">
            <div className="max-w-screen-md mx-auto bg-white dark:bg-gray-900 rounded-xl p-8 border border-gray-200 dark:border-gray-800">
              <h1 className="text-2xl font-bold text-[#0B1221] dark:text-white mb-4">Privacy Policy</h1>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                VINdicated.ai is committed to protecting your privacy. We do not collect personal information, sell data, or track you across the web.
                Your journey progress is stored locally on your device. We may collect anonymous usage analytics to improve the app.
              </p>
            </div>
          </div>
        } />
        <Route path="/terms" element={
          <div className="min-h-screen bg-gray-50 dark:bg-gray-950 p-8 pb-24">
            <div className="max-w-screen-md mx-auto bg-white dark:bg-gray-900 rounded-xl p-8 border border-gray-200 dark:border-gray-800">
              <h1 className="text-2xl font-bold text-[#0B1221] dark:text-white mb-4">Terms of Service</h1>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                By using VINdicated.ai, you agree that this app provides educational information only and is not legal, financial, or professional advice.
                All savings estimates are potential and based on typical scenarios. Actual results may vary.
              </p>
            </div>
          </div>
        } />
        
        {/* Catch-all redirect for any other route */}
        <Route path="*" element={<Navigate to="/public-tools" replace />} />
      </Routes>
      {!isStandalone && <BottomNav />}
    </div>
  );
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
      <JourneyProvider>
        <CompareProvider>
        <BrowserRouter>
          <AppLayout />
        </BrowserRouter>
        </CompareProvider>
      </JourneyProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;